import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Loader2,
  Save,
  Check,
  Upload,
  Image,
  X
} from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TRADEMARK_STATUSES = [
  { id: "registered", name: "Registered", description: "Trademark is registered and protected" },
  { id: "examination", name: "Examination", description: "Under examination by UK IPO" },
  { id: "published", name: "Application Published", description: "Application published for opposition" },
  { id: "opposed", name: "Opposed", description: "Opposition filed against the trademark" },
  { id: "dead", name: "Dead", description: "Trademark application abandoned or cancelled" }
];

const TRADEMARK_TYPES = [
  { id: "word", name: "Word Mark", description: "Text-only trademark" },
  { id: "logo", name: "Logo/Device Mark", description: "Image-only trademark" },
  { id: "combined", name: "Combined Mark", description: "Text and image combined" }
];

export const TrademarkForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const logoInputRef = useRef(null);
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formOwner, setFormOwner] = useState("");
  const [formContactEmail, setFormContactEmail] = useState("");
  const [formContactPhone, setFormContactPhone] = useState("");
  const [formRegNumber, setFormRegNumber] = useState("");
  const [formRegDate, setFormRegDate] = useState("");
  const [formTrademarkStatus, setFormTrademarkStatus] = useState("registered");
  const [formTrademarkType, setFormTrademarkType] = useState("word");
  const [formLogoUrl, setFormLogoUrl] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [errors, setErrors] = useState({});
  // New fields for watching service and expiry tracking
  const [formWatchingSearchRegistered, setFormWatchingSearchRegistered] = useState(false);
  const [formWatchingSearchExpiryDate, setFormWatchingSearchExpiryDate] = useState("");
  const [formRegistrationExpiryDate, setFormRegistrationExpiryDate] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const categoriesRes = await axios.get(`${API}/categories`);
      setCategories(categoriesRes.data);

      if (isEditing && id) {
        const trademarkRes = await axios.get(`${API}/trademarks/${id}`);
        setFormName(trademarkRes.data.name || "");
        setFormDescription(trademarkRes.data.description || "");
        setFormOwner(trademarkRes.data.owner || "");
        setFormContactEmail(trademarkRes.data.contact_email || "");
        setFormContactPhone(trademarkRes.data.contact_phone || "");
        setSelectedCategories(trademarkRes.data.categories || []);
        setFormRegNumber(trademarkRes.data.registration_number || "");
        setFormRegDate(trademarkRes.data.registration_date || "");
        setFormTrademarkStatus(trademarkRes.data.trademark_status || "registered");
        setFormTrademarkType(trademarkRes.data.trademark_type || "word");
        // Load new fields
        setFormWatchingSearchRegistered(trademarkRes.data.watching_search_registered || false);
        setFormWatchingSearchExpiryDate(trademarkRes.data.watching_search_expiry_date || "");
        setFormRegistrationExpiryDate(trademarkRes.data.registration_expiry_date || "");
        if (trademarkRes.data.logo_url) {
          setFormLogoUrl(trademarkRes.data.logo_url);
          setLogoPreview(trademarkRes.data.logo_url);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
      navigate('/trademarks');
    } finally {
      setLoading(false);
    }
  }, [id, isEditing, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formName.trim()) {
      newErrors.name = "Trademark name is required";
    }
    
    if (!formOwner.trim()) {
      newErrors.owner = "Owner is required";
    }
    
    if (!formContactEmail.trim()) {
      newErrors.contactEmail = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formContactEmail.trim())) {
      newErrors.contactEmail = "Please enter a valid email address";
    }
    
    if (!formContactPhone.trim()) {
      newErrors.contactPhone = "Phone number is required";
    }
    
    if (!formRegNumber.trim()) {
      newErrors.regNumber = "Registration number is required";
    }
    
    if (!formRegDate.trim()) {
      newErrors.regDate = "Registration date is required";
    }
    
    if (selectedCategories.length === 0) {
      newErrors.categories = "At least one category must be selected";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PNG, JPEG, or WebP image");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingLogo(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result;
        setLogoPreview(base64String);
        setFormLogoUrl(base64String);
        
        // Auto-set trademark type if not already set
        if (formTrademarkType === "word") {
          setFormTrademarkType("combined");
        }
        
        setUploadingLogo(false);
        toast.success("Logo uploaded successfully");
      };
      reader.onerror = () => {
        toast.error("Failed to read image file");
        setUploadingLogo(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error("Failed to upload logo");
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setFormLogoUrl("");
    setLogoPreview(null);
    if (formTrademarkType === "logo") {
      setFormTrademarkType("word");
    } else if (formTrademarkType === "combined") {
      setFormTrademarkType("word");
    }
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      const formData = {
        name: formName,
        description: formDescription,
        owner: formOwner,
        contact_email: formContactEmail,
        contact_phone: formContactPhone,
        categories: selectedCategories,
        registration_number: formRegNumber,
        registration_date: formRegDate,
        trademark_status: formTrademarkStatus,
        trademark_type: formTrademarkType,
        logo_url: formLogoUrl || null,
        // New fields
        watching_search_registered: formWatchingSearchRegistered,
        watching_search_expiry_date: formWatchingSearchExpiryDate || null,
        registration_expiry_date: formRegistrationExpiryDate || null
      };

      if (isEditing) {
        await axios.put(`${API}/trademarks/${id}`, formData);
        toast.success("Trademark updated successfully");
      } else {
        await axios.post(`${API}/trademarks`, formData);
        toast.success("Trademark created successfully");
      }
      navigate('/trademarks');
    } catch (error) {
      console.error("Error saving trademark:", error);
      const errorMsg = error.response?.data?.detail || "Failed to save trademark";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryClick = useCallback((categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(c => c !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "registered": return "bg-emerald-100 text-emerald-700 border-emerald-200";
      case "examination": return "bg-blue-100 text-blue-700 border-blue-200";
      case "published": return "bg-purple-100 text-purple-700 border-purple-200";
      case "opposed": return "bg-orange-100 text-orange-700 border-orange-200";
      case "dead": return "bg-slate-100 text-slate-500 border-slate-200";
      default: return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="trademark-form-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="trademark-form-page">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/trademarks')}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Trademarks
        </button>
        <h1 className="text-3xl md:text-4xl font-heading font-black text-slate-900 tracking-tight">
          {isEditing ? "Edit Trademark" : "Add New Trademark"}
        </h1>
        <p className="text-slate-500 mt-1">
          {isEditing ? "Update your trademark details" : "Register a trademark for monitoring"}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl">
        <div className="swiss-card space-y-6">
          {/* Name */}
          <div>
            <Label htmlFor="name" className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
              Trademark Name *
            </Label>
            <Input
              id="name"
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              className="input-swiss mt-2"
              placeholder="Enter trademark name"
              data-testid="trademark-name-input"
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description" className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
              Description
            </Label>
            <Textarea
              id="description"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              className="input-swiss mt-2 min-h-[100px]"
              placeholder="Describe your trademark and its usage"
              data-testid="trademark-description-input"
            />
          </div>

          {/* Owner */}
          <div>
            <Label htmlFor="owner" className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
              Owner <span className="text-red-500">*</span>
            </Label>
            <Input
              id="owner"
              type="text"
              value={formOwner}
              onChange={(e) => setFormOwner(e.target.value)}
              className={`input-swiss mt-2 ${errors.owner ? 'border-red-500' : ''}`}
              placeholder="e.g., Company Name Ltd"
              data-testid="trademark-owner-input"
            />
            {errors.owner && <p className="text-red-500 text-xs mt-1">{errors.owner}</p>}
          </div>

          {/* Contact Email */}
          <div>
            <Label htmlFor="contact_email" className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
              Contact Email <span className="text-red-500">*</span>
            </Label>
            <Input
              id="contact_email"
              type="email"
              value={formContactEmail}
              onChange={(e) => setFormContactEmail(e.target.value)}
              className={`input-swiss mt-2 ${errors.contactEmail ? 'border-red-500' : ''}`}
              placeholder="e.g., contact@company.com"
              data-testid="trademark-contact-email-input"
            />
            {errors.contactEmail && <p className="text-red-500 text-xs mt-1">{errors.contactEmail}</p>}
          </div>

          {/* Contact Phone */}
          <div>
            <Label htmlFor="contact_phone" className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
              Contact Phone <span className="text-red-500">*</span>
            </Label>
            <Input
              id="contact_phone"
              type="tel"
              value={formContactPhone}
              onChange={(e) => setFormContactPhone(e.target.value)}
              className={`input-swiss mt-2 ${errors.contactPhone ? 'border-red-500' : ''}`}
              placeholder="e.g., +44 20 1234 5678"
              data-testid="trademark-contact-phone-input"
            />
            {errors.contactPhone && <p className="text-red-500 text-xs mt-1">{errors.contactPhone}</p>}
          </div>

          {/* Trademark Status */}
          <div>
            <Label className="text-sm font-semibold text-slate-700 uppercase tracking-wider block mb-2">
              Trademark Status
            </Label>
            <Select value={formTrademarkStatus} onValueChange={setFormTrademarkStatus}>
              <SelectTrigger className="input-swiss" data-testid="trademark-status-select">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {TRADEMARK_STATUSES.map((status) => (
                  <SelectItem key={status.id} value={status.id}>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 text-xs font-mono border ${getStatusColor(status.id)}`}>
                        {status.name}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 mt-2">
              {TRADEMARK_STATUSES.find(s => s.id === formTrademarkStatus)?.description}
            </p>
          </div>

          {/* Trademark Type */}
          <div>
            <Label className="text-sm font-semibold text-slate-700 uppercase tracking-wider block mb-2">
              Trademark Type
            </Label>
            <Select value={formTrademarkType} onValueChange={setFormTrademarkType}>
              <SelectTrigger className="input-swiss" data-testid="trademark-type-select">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {TRADEMARK_TYPES.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    <span>{type.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 mt-2">
              {TRADEMARK_TYPES.find(t => t.id === formTrademarkType)?.description}
            </p>
          </div>

          {/* Logo Upload */}
          {(formTrademarkType === "logo" || formTrademarkType === "combined") && (
            <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <Label className="text-sm font-semibold text-indigo-700 uppercase tracking-wider block mb-3">
                <Image className="w-4 h-4 inline-block mr-2" />
                Trademark Logo
              </Label>
              <p className="text-sm text-slate-600 mb-4">
                Upload your logo image for visual similarity analysis. Accepted formats: PNG, JPEG, WebP (max 5MB)
              </p>
              
              {logoPreview ? (
                <div className="flex items-start gap-4">
                  <div className="w-32 h-32 bg-white border border-slate-200 rounded-lg overflow-hidden flex items-center justify-center">
                    <img 
                      src={logoPreview} 
                      alt="Logo preview" 
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => logoInputRef.current?.click()}
                      className="text-slate-600"
                      data-testid="change-logo-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Change Logo
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleRemoveLogo}
                      className="text-red-600 hover:text-red-700"
                      data-testid="remove-logo-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Remove Logo
                    </Button>
                  </div>
                </div>
              ) : (
                <div 
                  className="border-2 border-dashed border-indigo-300 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-100 transition-colors"
                  onClick={() => logoInputRef.current?.click()}
                  data-testid="logo-upload-area"
                >
                  {uploadingLogo ? (
                    <Loader2 className="w-8 h-8 mx-auto text-indigo-500 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mx-auto text-indigo-400 mb-2" />
                      <p className="text-sm text-slate-600">Click to upload logo image</p>
                      <p className="text-xs text-slate-400 mt-1">PNG, JPEG, WebP (max 5MB)</p>
                    </>
                  )}
                </div>
              )}
              
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handleLogoUpload}
                className="hidden"
                data-testid="logo-file-input"
              />
            </div>
          )}

          {/* Registration Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="registration_number" className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                Registration Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="registration_number"
                type="text"
                value={formRegNumber}
                onChange={(e) => setFormRegNumber(e.target.value)}
                className={`input-swiss mt-2 ${errors.regNumber ? 'border-red-500' : ''}`}
                placeholder="e.g., UK00003123456"
                data-testid="trademark-reg-number-input"
              />
              {errors.regNumber && <p className="text-red-500 text-xs mt-1">{errors.regNumber}</p>}
            </div>
            <div>
              <Label htmlFor="registration_date" className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                Registration Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="registration_date"
                type="date"
                value={formRegDate}
                onChange={(e) => setFormRegDate(e.target.value)}
                className={`input-swiss mt-2 ${errors.regDate ? 'border-red-500' : ''}`}
                data-testid="trademark-reg-date-input"
              />
              {errors.regDate && <p className="text-red-500 text-xs mt-1">{errors.regDate}</p>}
            </div>
          </div>

          {/* Watching Service & Expiry Dates */}
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <h3 className="text-sm font-semibold text-amber-800 uppercase tracking-wider mb-4">
              Watching Service & Expiry Dates
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Watching Search Registered */}
              <div>
                <Label className="text-sm font-semibold text-slate-700 uppercase tracking-wider block mb-2">
                  Watching Search Registered
                </Label>
                <Select 
                  value={formWatchingSearchRegistered ? "yes" : "no"} 
                  onValueChange={(val) => setFormWatchingSearchRegistered(val === "yes")}
                >
                  <SelectTrigger className="input-swiss" data-testid="watching-search-registered-select">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1">Is a watching search service registered?</p>
              </div>

              {/* Watching Search Expiry Date */}
              <div>
                <Label htmlFor="watching_search_expiry" className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                  Watching Search Expiry Date
                </Label>
                <Input
                  id="watching_search_expiry"
                  type="date"
                  value={formWatchingSearchExpiryDate}
                  onChange={(e) => setFormWatchingSearchExpiryDate(e.target.value)}
                  className="input-swiss mt-2"
                  data-testid="watching-search-expiry-input"
                />
                <p className="text-xs text-slate-400 mt-1">When does the watching service expire?</p>
              </div>

              {/* Registration Expiry Date */}
              <div>
                <Label htmlFor="registration_expiry" className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                  Registration Expiry Date
                </Label>
                <Input
                  id="registration_expiry"
                  type="date"
                  value={formRegistrationExpiryDate}
                  onChange={(e) => setFormRegistrationExpiryDate(e.target.value)}
                  className="input-swiss mt-2"
                  data-testid="registration-expiry-input"
                />
                <p className="text-xs text-slate-400 mt-1">When does the trademark registration expire?</p>
              </div>
            </div>
          </div>

          {/* Categories */}
          <div>
            <Label className="text-sm font-semibold text-slate-700 uppercase tracking-wider block mb-3">
              Nice Classification Categories <span className="text-red-500">*</span>
            </Label>
            <p className="text-sm text-slate-500 mb-4">
              Select the categories that apply to your trademark
            </p>
            {errors.categories && <p className="text-red-500 text-xs mb-2">{errors.categories}</p>}
            <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto p-1 ${errors.categories ? 'border border-red-500 rounded' : ''}`} data-testid="categories-list">
              {categories.map((category) => {
                const isSelected = selectedCategories.includes(category.id);
                return (
                  <div
                    key={category.id}
                    role="checkbox"
                    aria-checked={isSelected}
                    tabIndex={0}
                    className={`flex items-start gap-3 p-3 border cursor-pointer select-none ${
                      isSelected
                        ? 'border-slate-900 bg-slate-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => handleCategoryClick(category.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCategoryClick(category.id);
                      }
                    }}
                    data-testid={`category-${category.id}`}
                  >
                    <div className={`w-4 h-4 mt-0.5 border flex-shrink-0 flex items-center justify-center ${
                      isSelected ? 'bg-slate-900 border-slate-900' : 'border-slate-300 bg-white'
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div>
                      <p className="font-mono text-xs text-slate-400">Class {category.id}</p>
                      <p className="font-semibold text-sm text-slate-900">{category.name}</p>
                      <p className="text-xs text-slate-500">{category.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4 pt-4 border-t border-slate-200">
            <Button
              type="submit"
              disabled={saving}
              className="btn-primary flex items-center gap-2"
              data-testid="save-trademark-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {saving ? "SAVING..." : "SAVE TRADEMARK"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/trademarks')}
              className="btn-secondary"
              data-testid="cancel-btn"
            >
              CANCEL
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
